import React, { useEffect, useRef, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation, useNavigationType, Navigate } from 'react-router-dom';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import Header from './components/Header';
import ProductList from './components/ProductList';
import ProductDetail from './components/ProductDetail';
import Cart from './components/Cart';
import Checkout from './components/Checkout';
import CategoryPage from './components/CategoryPage';
import ScratchCardPage from './components/ScratchCardPage'; 
import ScratchCardOnlyPage from './components/ScratchCardOnlyPage';
import ScratchCardTrackedPage from './components/ScratchCardTrackedPage';
import Breadcrumb from './components/Breadcrumb';
import PageContent from './components/PageContent';
import TrackingProvider from './components/TrackingProvider';
import VisualCatalogPage from './components/VisualCatalogPage';
import DemoScratchCardPage from './components/DemoScratchCardPage';
import MinisitePage from './components/MinisitePage';
import MinisiteEditor from './components/MinisiteEditor';
import MinisitePreviewPage from './components/MinisitePreviewPage';
import MinisiteCatalogPage from './components/MinisiteCatalogPage';
import DemoMinisitePage from './components/DemoMinisitePage';
import LifeEventPage from './components/LifeEventPage';
import FaqPage from './components/FaqPage';
import { LIFE_EVENTS } from './utils/lifeEvents';
import { MINISITE_OCCASIONS, MINISITE_OCCASION_KEYS, MINISITE_HUB_PATH } from './utils/minisiteOccasions';
import DemoBanner from './components/DemoBanner';
import TrackerConsent, { CookieSettingsLink } from './components/TrackerConsent';


import { CartProvider } from './context/CartContext';
import { ThemeProvider } from '@mui/material/styles';
import theme from './theme';
import { PayPalScriptProvider } from "@paypal/react-paypal-js";
import './App.css';

// Écrans admin chargés à la demande (code-splitting) : ils ne pèsent plus dans le bundle
// principal téléchargé par les visiteurs de la boutique (ROADMAP-2026-09.md, A1.4-perf).
const EditCard = lazy(() => import('./components/EditCard'));
const OrderManagement = lazy(() => import('./components/OrderManagement'));
const PromoCodeManagement = lazy(() => import('./components/PromoCodeManagement'));
const AdminCreateCard = lazy(() => import('./components/AdminCreateCard'));
const CartesV2Table = lazy(() => import('./components/CartesV2Table'));
// Pages admin « Trackers » et « Tunnel d'acquisition » (2026-09-19). Cette dernière remplace
// l'ancienne page Analytics.js : /analytics y redirige (voir la route plus bas).
const AdminTrackers = lazy(() => import('./components/AdminTrackers'));
const AdminAcquisition = lazy(() => import('./components/AdminAcquisition'));
const AdminCatalogManagement = lazy(() => import('./components/AdminCatalogManagement'));
const AdminVisualLibrary = lazy(() => import('./components/AdminVisualLibrary'));


// PayPal options
const initialOptions = {
  "client-id": "AWh5b3onMTmuYVIYhNZ8B8d2QX7S7IShRJAwWde70q0lu8KUnOFsV1f85B9bZFFcwy-XjdQi5yECzY2G",
  currency: "EUR",
  intent: "capture",
};

// Composant pour gérer l'affichage du Header en fonction de l'URL
function Layout({ cartIconRef }) {
  const location = useLocation(); // Hook pour obtenir l'URL actuelle
  const navigationType = useNavigationType();

  // index.html contient une <meta name="description"> générique (utile aux robots qui n'exécutent pas
  // le JavaScript et à l'aperçu de lien). Une fois l'appli lancée, chaque page pose la sienne (Seo.js,
  // App.js) : on retire la générique pour n'avoir qu'UNE description par page — avec deux, Google peut
  // retenir la mauvaise. Les balises posées par l'appli portent data-rh et ne sont pas touchées.
  useEffect(() => {
    document.querySelectorAll('meta[name="description"]:not([data-rh])').forEach((m) => m.remove());
  }, []);

  // Changement de page : on arrive EN HAUT de la nouvelle page (sinon le navigateur garde la
  // hauteur de défilement de la page précédente). Immédiat, sans animation. Exceptions : le bouton
  // « Précédent » (POP), où le navigateur rend sa position au visiteur, et les changements qui ne
  // touchent que l'ancre ou les paramètres (onglets #naissance, filtre ?theme=).
  useEffect(() => {
    if (navigationType !== 'POP') window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [location.pathname, navigationType]);

  // Cacher le Header et le Breadcrumb pour la route "/carte-a-gratter/:id" et "/carte-surprise/:id"
  // Idem pour la page publique du mini-site de mariage (lien envoyé aux invités, plein écran).
  const hideHeader = location.pathname.startsWith('/carte-a-gratter/') || location.pathname.startsWith('/carte-surprise/') || location.pathname.startsWith('/i/') || location.pathname === '/apercu-mini-site';

  return (
    <>
    {/* Tracking automatique des visites */}
    <TrackingProvider />
    {/* Bandeau de consentement + chargement des traceurs configurés dans /trackers */}
    <TrackerConsent />
    
    {/* SEO Helmet - Optimisé pour le référencement */}
    <Helmet>
        <title>Faire-part animés et cartes à gratter numériques | Joli Ptit Mot</title>
        <meta
          name="description"
          content="Faire-part et invitations animés (mariage, naissance, baptême, anniversaire) avec réponses des invités, et cartes à gratter numériques. À partager par WhatsApp."
        />
        <meta name="robots" content="index, follow" />
        <meta name="keywords" content="faire-part animé, faire-part de mariage digital, faire-part de naissance digital, invitation animée, invitation baptême, invitation anniversaire, carte à gratter numérique, annonce grossesse, carte digitale mariage, annonce naissance originale, carte à gratter virtuelle, annonce baptême, surprise grossesse, carte grattage en ligne" />
        <link rel="canonical" href="https://joli-mot.fr/" />
        
        {/* Open Graph pour les réseaux sociaux */}
        <meta property="og:title" content="Faire-part animés et cartes à gratter numériques | Joli Ptit Mot" />
        <meta property="og:description" content="Faire-part et invitations animés avec réponses des invités, et cartes à gratter numériques : annoncez et invitez de façon unique, par simple lien." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://joli-mot.fr/" />
        <meta property="og:locale" content="fr_FR" />
        <meta property="og:site_name" content="Joli Ptit Mot" />
        <meta property="og:image" content="https://joli-mot.fr/assets_react/minisites/faire-part-mariage-anime-fleurs-bleues-aquarelle.jpg" />
        <meta name="twitter:image" content="https://joli-mot.fr/assets_react/minisites/faire-part-mariage-anime-fleurs-bleues-aquarelle.jpg" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Faire-part animés et cartes à gratter numériques | Joli Ptit Mot" />
        <meta name="twitter:description" content="Annoncez vos moments magiques avec un faire-part animé ou une carte à gratter numérique !" />
        {/* Qui nous sommes, en données structurées (moteurs de recherche et assistants IA). */}
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'OnlineStore',
          name: 'Joli Ptit Mot',
          alternateName: 'joli-mot.fr',
          url: 'https://joli-mot.fr/',
          logo: 'https://joli-mot.fr/logo512.png',
          description: 'Boutique française de faire-part et invitations animés (mariage, save-the-date, naissance, baptême, communion, anniversaire) et de cartes à gratter numériques, à partager par lien, WhatsApp, SMS ou QR code.',
          areaServed: 'FR',
          inLanguage: 'fr-FR',
        })}</script>
      </Helmet>
      {/* Afficher le Header uniquement si on n'est pas sur la route "/carte-a-gratter" */}
      {!hideHeader && <Header cartIconRef={cartIconRef} />}
      {!hideHeader && <DemoBanner />}
      <div className='pageContainer' style={{ padding: '0 20px', minHeight: `calc(100vh - 126px)`, paddingBottom:'50px'}}>
        {!hideHeader && <Breadcrumb />} {/* Cacher le Breadcrumb aussi si hideHeader est vrai */}
        <Suspense fallback={<div style={{ padding: 40, textAlign: 'center' }}>Chargement…</div>}>
        <Routes>
          <Route path="/" element={<ProductList />} />
          <Route path="/produit/:url" element={<ProductDetail cartIconRef={cartIconRef} />} />
          <Route path="/cart" element={<Cart />} />
          <Route
            path="/checkout"
            element={(
              <PayPalScriptProvider options={initialOptions}>
                <Checkout />
              </PayPalScriptProvider>
            )}
          />
          <Route path="/category/:categoryId" element={<CategoryPage />} />
          <Route path="/carte-a-gratter-digitale-questions-frequentes" element={<FaqPage />} />
          <Route path="/contact" element={<PageContent url="contact" />} />
          <Route path="/voir-ma-carte/:id" element={<ScratchCardPage />} />
          <Route path="/i/:slug" element={<MinisitePage />} />
          <Route path="/apercu-mini-site" element={<MinisitePreviewPage />} />
          {/* Faire-part et invitations animés : page chapeau, une page par occasion, démo */}
          <Route path={MINISITE_HUB_PATH} element={<MinisiteCatalogPage />} />
          {MINISITE_OCCASION_KEYS.map((key) => (
            <Route key={key} path={MINISITE_OCCASIONS[key].hub.path} element={<MinisiteCatalogPage occasionKey={key} />} />
          ))}
          <Route path="/exemple-faire-part-anime" element={<DemoMinisitePage />} />
          {/* Pages par événement de vie : toutes les gammes pour un mariage, une naissance… */}
          {LIFE_EVENTS.map((event) => (
            <Route key={event.key} path={event.path} element={<LifeEventPage eventKey={event.key} />} />
          ))}
          <Route path="/personnaliser-mini-site/:slug/:token" element={<MinisiteEditor />} />
          <Route path="/gestion-commandes" element={<OrderManagement />} />
          <Route path="/gestion-promos" element={<PromoCodeManagement />} />
          <Route path="/creer-card" element={<AdminCreateCard />} />
          <Route path="/list-cards" element={<CartesV2Table />} />
          <Route path="/analytics" element={<Navigate to="/tunnel-acquisition" replace />} />
          <Route path="/tunnel-acquisition" element={<AdminAcquisition />} />
          <Route path="/trackers" element={<AdminTrackers />} />
          <Route path="/gestion-catalogue" element={<AdminCatalogManagement />} />
          <Route path="/bibliotheque-visuels" element={<AdminVisualLibrary />} />
          <Route path="/cartes-a-gratter-personnalisees" element={<VisualCatalogPage />} />
          <Route path="/exemple-carte-a-gratter" element={<DemoScratchCardPage />} />


          <Route path="/edit-card/:orderId/:cardId" element={<EditCard />} />  
          
          {/* Route sans Header qui affiche uniquement la carte à gratter */}
          <Route path="/carte-old/:id" element={<ScratchCardOnlyPage />} />
          
          {/* Route avec tracking des grattages */}
          <Route path="/carte-a-gratter/:id" element={<ScratchCardTrackedPage />} />
        </Routes>
        </Suspense>
      </div>
      {!hideHeader && <div className='footer'>Joli Ptit Mot -  Cartes numériques à gratter pour les mariages, les naissances, et les baptêmes.</div>}
      {!hideHeader && <CookieSettingsLink />}
    </>
  );
}

function App() {
  const cartIconRef = useRef(null);

  return (
    <HelmetProvider>
      <ThemeProvider theme={theme}>
        <CartProvider>
          <Router>
            {/* Placer Layout dans le Router pour utiliser useLocation */}
            <Layout cartIconRef={cartIconRef} />
          </Router>
        </CartProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
